import { motion } from "framer-motion";
import {
  CalendarDays,
  ClipboardList,
  GraduationCap,
  CloudUpload,
  MonitorCheck,
  Headphones,
  ShieldCheck,
  Users,
  Clock,
  BarChart3,
  Heart,
} from "lucide-react";
import { GET_STARTED_NOW_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

// -----------------------------------------------------------------------------
// GetStartedNow - Antera's 6-step onboarding process section.
// Placed near the end of the homepage, right before the Final CTA. Icons and
// per-step tone (light/mid/dark/night) are position-locked to slots. Titles,
// descriptions, benefits, and the trust line are CMS-editable via block.data
// (registry.js → get_started_now).
// -----------------------------------------------------------------------------

// Position-locked icon + tone map for the 6 step slots.
const STEP_META = [
  { Icon: CalendarDays,   tone: "light" },
  { Icon: ClipboardList,  tone: "light" },
  { Icon: GraduationCap,  tone: "light" },
  { Icon: CloudUpload,    tone: "mid" },
  { Icon: MonitorCheck,   tone: "dark" },
  { Icon: Headphones,     tone: "night" },
];

// Position-locked icon list for the 5 benefit slots.
const BENEFIT_ICONS = [ShieldCheck, Users, Clock, BarChart3, Heart];

// Circle tone map - progressively saturates so the last step reads as
// "long-term partner" (dark navy).
const CIRCLE_TONE = {
  light: { bg: "bg-sky-100/70", icon: "text-sky-600", badgeBg: "bg-sky-600", badgeText: "text-white" },
  mid:   { bg: "bg-sky-200/80", icon: "text-sky-700", badgeBg: "bg-sky-700", badgeText: "text-white" },
  dark:  { bg: "bg-sky-700",    icon: "text-white",   badgeBg: "bg-slate-900", badgeText: "text-white" },
  night: { bg: "bg-slate-900",  icon: "text-white",   badgeBg: "bg-slate-900", badgeText: "text-white" },
};

function StepCircle({ Icon, tone }) {
  const t = CIRCLE_TONE[tone] || CIRCLE_TONE.light;
  return (
    <div
      className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full ${t.bg} flex items-center justify-center shadow-sm`}
    >
      <Icon className={`h-11 w-11 sm:h-12 sm:w-12 ${t.icon}`} strokeWidth={1.8} />
    </div>
  );
}

function StepCard({ step, meta, num, isLast }) {
  const Icon = meta.Icon;
  const tone = meta.tone;
  const t = CIRCLE_TONE[tone] || CIRCLE_TONE.light;
  return (
    <div
      className="relative flex flex-col items-center text-center"
      data-testid={`get-started-step-${num}`}
    >
      <StepCircle Icon={Icon} tone={tone} />

      {!isLast && (
        <div
          aria-hidden
          className="hidden lg:block absolute top-14 sm:top-14 left-full h-6 w-full pointer-events-none"
        >
          <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="w-full h-full">
            <path d="M 2 10 L 92 10" stroke="#0284c7" strokeWidth="2" strokeDasharray="3 5" strokeLinecap="round" fill="none" />
            <path d="M 88 5 L 96 10 L 88 15" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </div>
      )}

      <div className="relative mt-6 w-full bg-white rounded-2xl px-4 py-5 sm:px-5 sm:py-6 shadow-sm ring-1 ring-slate-100">
        <div
          className={`absolute -top-4 left-1/2 -translate-x-1/2 h-8 w-8 rounded-full ${t.badgeBg} ${t.badgeText} flex items-center justify-center text-sm font-black shadow-md`}
          aria-label={`Step ${num}`}
        >
          {num}
        </div>
        <h3 className={`mt-2 text-[15px] sm:text-base font-extrabold ${tone === "night" ? "text-slate-900" : "text-sky-600"}`}>
          {step.title}
        </h3>
        <p className="mt-2 text-[13px] sm:text-[13.5px] leading-relaxed text-slate-600">
          {step.desc}
        </p>
      </div>
    </div>
  );
}

export default function GetStartedNow({ data }) {
  const c = mergeSection(GET_STARTED_NOW_DEFAULT, data);
  const steps =
    Array.isArray(c.steps) && c.steps.length > 0
      ? c.steps.slice(0, STEP_META.length)
      : GET_STARTED_NOW_DEFAULT.steps;
  const benefits =
    Array.isArray(c.benefits) && c.benefits.length > 0
      ? c.benefits.slice(0, BENEFIT_ICONS.length)
      : GET_STARTED_NOW_DEFAULT.benefits;

  return (
    <section
      data-testid="get-started-now-section"
      className="relative bg-white py-16 sm:py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-sky-50/60 to-transparent pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
          className="flex items-center justify-center gap-3"
          data-testid="get-started-eyebrow"
        >
          <span className="h-px w-10 bg-sky-600/70" aria-hidden />
          <span className="text-xs sm:text-sm font-bold tracking-[0.28em] text-sky-600 uppercase">
            {c.eyebrow}
          </span>
          <span className="h-px w-10 bg-sky-600/70" aria-hidden />
        </motion.div>

        {/* Two-tone heading */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mt-4 text-center text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]"
          data-testid="get-started-heading"
        >
          <span className="text-sky-600">{c.heading_lead}</span>{" "}
          <span className="text-slate-900">{c.heading_trail}</span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-4 max-w-3xl mx-auto text-center text-base sm:text-lg text-slate-600 leading-relaxed"
          data-testid="get-started-subtitle"
        >
          {c.subtitle}
        </motion.p>

        {/* 6-step timeline */}
        <div
          className="mt-14 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-y-12 gap-x-4 lg:gap-x-2"
          data-testid="get-started-steps"
        >
          {steps.map((step, i) => (
            <motion.div
              key={`step-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: 0.08 * i }}
            >
              <StepCard
                step={step}
                meta={STEP_META[i] || STEP_META[STEP_META.length - 1]}
                num={i + 1}
                isLast={i === steps.length - 1}
              />
            </motion.div>
          ))}
        </div>

        {/* Benefits strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="mt-16 lg:mt-20 bg-sky-50/70 rounded-2xl px-5 py-7 sm:px-8 sm:py-8"
          data-testid="get-started-benefits"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i] || BENEFIT_ICONS[BENEFIT_ICONS.length - 1];
              const slug = (b.title || `benefit-${i}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <div
                  key={`${slug}-${i}`}
                  className="flex items-start gap-3"
                  data-testid={`get-started-benefit-${slug}`}
                >
                  <span className="shrink-0 h-10 w-10 rounded-full bg-white ring-1 ring-sky-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-sky-600" strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-sky-700">{b.title}</p>
                    <p className="mt-0.5 text-[13px] text-slate-600 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Trust banner */}
        {c.trust_line ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 flex items-center justify-center gap-3 text-center"
            data-testid="get-started-trust"
          >
            <span className="shrink-0 h-8 w-8 rounded-md bg-sky-600 flex items-center justify-center">
              <ShieldCheck className="h-4.5 w-4.5 text-white" strokeWidth={2.2} />
            </span>
            <p className="text-sm sm:text-base font-semibold text-slate-900">
              {c.trust_line}
            </p>
          </motion.div>
        ) : null}
      </div>
    </section>
  );
}
